/**
 * Admin · Einstellungen · Watchdog — „Empfohlene Fokus-Events"
 * (Go-Live-Checkliste Nr. 18, 🤝 Mario + Francesco 11.09.2026)
 *
 * Die Standard-Conversions, die der ambix-Wizard beim Anlegen einer Domain
 * vorschlaegt. Gepflegt je Datenbank-Umgebung (app_settings der aktiven
 * Umgebung, Schluessel watchdog_recommended_focus_events). Vorgeschlagen wird
 * nur, was im GTM der Domain tatsaechlich vorkommt — den Abgleich macht das
 * Backend in POST /api/watchdog/domains/preview, nicht diese Seite.
 */
import { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import { api } from '../../../api/client.js';
import { colors } from '../../../styles/theme.js';

interface RecommendedFocusEvents {
  names: string[];
  /** `default`, solange in dieser Umgebung nichts hinterlegt ist. */
  source: 'app_settings' | 'default';
}

const toText = (names: string[]): string => names.join(', ');
const toNames = (text: string): string[] =>
  text.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);

export default function RecommendedFocusEventsCard() {
  const [current, setCurrent] = useState<RecommendedFocusEvents | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const load = async () => {
    const res = await api.get<RecommendedFocusEvents>('/watchdog/recommended-focus-events');
    if (!res.success) { setError(res.error.message); return; }
    setCurrent(res.data);
    setText(toText(res.data.names));
    setError(null);
  };

  useEffect(() => { void load(); }, []);

  const dirty = current !== null && toText(toNames(text)) !== toText(current.names);

  const save = async () => {
    setBusy(true);
    setSaved(null);
    const res = await api.put<RecommendedFocusEvents>('/watchdog/recommended-focus-events', { names: toNames(text) });
    setBusy(false);
    if (!res.success) { setError(res.error.message); return; }
    setCurrent(res.data);
    setText(toText(res.data.names));
    setError(null);
    setSaved(res.data.source === 'default'
      ? 'Liste geleert — die Standardliste gilt wieder.'
      : 'Empfehlungsliste gespeichert. Sie wirkt ab der nächsten Vorschau im ambix-Wizard.');
  };

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Empfohlene Fokus-Events</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Die Conversions, die der ambix-Wizard beim Anlegen einer Domain vorschlägt. Getroffen wird ein
        Event über seinen Namen oder seinen Seed-Typ (purchase, add_to_cart, form_sent, newsletter);
        vorgeschlagen wird nur, was im GTM der Domain tatsächlich vorkommt. Gilt für die{' '}
        <strong>aktive Datenbank-Umgebung</strong>.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSaved(null)}>{saved}</Alert>}

      <TextField
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="purchase, form_sent, generate_lead, newsletter_signup"
        helperText="Event-Namen, durch Komma getrennt. Leer speichern stellt die Standardliste wieder her."
        size="small"
        fullWidth
        multiline
        minRows={2}
        disabled={busy || current === null}
      />

      <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
        <Button
          variant="contained" size="small" startIcon={<SaveIcon />}
          onClick={() => void save()}
          disabled={!dirty || busy}
        >
          Speichern
        </Button>
        <Button
          size="small" startIcon={<UndoIcon />}
          onClick={() => { if (current) setText(toText(current.names)); }}
          disabled={!dirty || busy}
        >
          Verwerfen
        </Button>
        <Box sx={{ flex: 1 }} />
        {current && (
          <Chip
            size="small"
            variant="outlined"
            label={current.source === 'default'
              ? 'Standardliste (nichts hinterlegt)'
              : `${current.names.length} Einträge in app_settings`}
          />
        )}
      </Stack>
    </Paper>
  );
}
